import React, { useState, useEffect, useContext } from "react";
import { UserContext } from "../../../contexts/user.context";
import { Get, Patch, handleError } from "../../../utils/fetch.util";
import { useTranslation } from "react-i18next";
import { format } from "date-fns";
import LoadingData from "../../../components/loadingData/loadingData.component";
import {
  FiPhone,
  FiCalendar,
  FiUser,
  FiTruck,
  FiX,
  FiCheck,
  FiXCircle,
  FiClock,
} from "react-icons/fi";
import Select from "react-select";
import Image from "../../../components/Image/Image.component";

interface VisitorRequest {
  id: number;
  name: string;
  reason: string;
  nationalId: string;
  phone: string;
  qrcode: string;
  qrcode_url: string;
  startDate: string;
  endDate: string;
  allowVehicle: boolean;
  current_status: "pending" | "approved" | "rejected";
  createdAt: string;
  updatedAt: string;
}

export default function VisitorRequests() {
  const { t } = useTranslation();
  const { getValidToken, userData } = useContext(UserContext);

  // State management
  const [visitorRequests, setVisitorRequests] = useState<VisitorRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [pageSize] = useState(10);
  const [activeTab, setActiveTab] = useState<"visitor" | "vehicle">("visitor");
  const [approvalStatus, setApprovalStatus] = useState("");

  // Status update modal state
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [statusLoading, setStatusLoading] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState<VisitorRequest | null>(
    null
  );
  const [newStatus, setNewStatus] = useState<
    "pending" | "approved" | "rejected" | ""
  >("");
  const [statusReason, setStatusReason] = useState("");

  const sites =
    userData[0]?.user_sites_relationShip?.map((site: any) => site?.site) || [];
  const sitesOptions = sites.map((site: any) => ({
    value: site.site_id,
    label: site.site_name,
  }));

  const [siteId, setSiteId] = useState<{ value: number; label: string } | null>(
    null
  );

  // Fetch all visitor requests
  const fetchVisitorRequests = async () => {
    setLoading(true);
    try {
      const queryParams = new URLSearchParams({
        page: currentPage.toString(),
        pageItemsCount: pageSize.toString(),
        vehicle_only: activeTab === "vehicle" ? "true" : "false",
        ...(searchTerm && { search: searchTerm }),
        ...(siteId?.value && { site_id: siteId.value.toString() }),
        ...(startDate && { startDate }),
        ...(endDate && { endDate }),
        ...(approvalStatus && {
          approval_status: JSON.stringify([approvalStatus]),
        }),
      } as any);

      const response = await Get(
        `visitor-requests/get-all-visitor-requests?${queryParams}`,
        getValidToken()
      );

      if (response.error || !response.data) {
        handleError(response);
        return;
      }

      const apiResponse: VisitorRequest[] = response.data;
      setVisitorRequests(apiResponse);
      setTotalCount(response.count);
    } catch (error) {
      console.error("Error fetching visitor requests:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVisitorRequests();
  }, [
    currentPage,
    searchTerm,
    siteId,
    startDate,
    endDate,
    activeTab,
    approvalStatus,
  ]);

  // Open status update modal
  const openStatusModal = (request: VisitorRequest) => {
    setSelectedRequest(request);
    setNewStatus(request.current_status);
    setStatusReason("");
    setShowStatusModal(true);
  };

  // Update visitor request status
  const updateRequestStatus = async () => {
    if (!selectedRequest) return;

    setStatusLoading(true);
    try {
      const response = await Patch(
        "visitor-requests/update-visitor-request-status",
        {
          id: selectedRequest.id,
          status: newStatus,
          reason: statusReason,
        },
        getValidToken()
      );

      if (response.error || !response.data) {
        handleError(response);
        return;
      }

      // Close modal and refresh list
      setShowStatusModal(false);
      setSelectedRequest(null);
      fetchVisitorRequests();
    } catch (error) {
      console.error("Error updating visitor request status:", error);
    } finally {
      setStatusLoading(false);
    }
  };

  // Status badge component
  const StatusBadge = ({ status }: { status: string }) => {
    const getStatusStyles = () => {
      switch (status.toLowerCase()) {
        case "approved":
          return "bg-green-100 text-green-700 border-green-200";
        case "rejected":
          return "bg-red-100 text-red-700 border-red-200";
        case "pending":
        default:
          return "bg-yellow-100 text-yellow-700 border-yellow-200";
      }
    };

    const getStatusIcon = () => {
      switch (status.toLowerCase()) {
        case "approved":
          return <FiCheck className="w-3 h-3 mr-1" />;
        case "rejected":
          return <FiXCircle className="w-3 h-3 mr-1" />;
        case "pending":
        default:
          return <FiClock className="w-3 h-3 mr-1" />;
      }
    };

    return (
      <span
        className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border ${getStatusStyles()}`}
      >
        {getStatusIcon()}
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  console.log(visitorRequests);

  // Visitor request card component
  const VisitorRequestCard = ({ request }: { request: VisitorRequest }) => (
    <div className="bg-white rounded-lg shadow-md border border-gray-200 p-6 hover:shadow-lg transition-shadow">
      <div className="flex justify-between items-start mb-4">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 font-semibold">
            {request.name.charAt(0).toUpperCase()}
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">
              {request.name}
            </h3>
            <p className="text-sm text-gray-500">ID: {request.nationalId}</p>
          </div>
        </div>
        <StatusBadge status={request.current_status} />
      </div>

      <div className="space-y-3">
        <div className="flex items-center text-gray-600">
          <Image
            src={request?.qrcode_url}
            alt={request.name}
            className="w-[75%] mx-auto"
          />
        </div>

        <div className="flex items-center text-gray-600">
          <FiUser className="w-4 h-4 mr-2" />
          <span className="text-sm">{request.reason}</span>
        </div>

        <div className="flex items-center text-gray-600">
          <FiPhone className="w-4 h-4 mr-2" />
          <span className="text-sm">{request.phone}</span>
        </div>

        <div className="flex items-center text-gray-600">
          <FiCalendar className="w-4 h-4 mr-2" />
          <span className="text-sm">
            {format(new Date(request.startDate), "MMM dd, yyyy")} -{" "}
            {format(new Date(request.endDate), "MMM dd, yyyy")}
          </span>
        </div>

        {request.allowVehicle && (
          <div className="flex items-center text-green-600">
            <FiTruck className="w-4 h-4 mr-2" />
            <span className="text-sm">Vehicle allowed</span>
          </div>
        )}

        <div className="text-xs text-gray-400 mt-3">
          Created: {format(new Date(request.createdAt), "MMM dd, yyyy HH:mm")}
        </div>
      </div>

      <div className="mt-4 pt-4 border-t border-gray-100">
        <button
          onClick={() => openStatusModal(request)}
          className="w-full px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors"
        >
          Update Status
        </button>
      </div>
    </div>
  );

  // Pagination component
  const Pagination = () => {
    const totalPages = Math.ceil(totalCount / pageSize);

    return (
      <div className="flex items-center justify-between mt-6">
        <div className="text-sm text-gray-700">
          Showing {(currentPage - 1) * pageSize + 1} to{" "}
          {Math.min(currentPage * pageSize, totalCount)} of {totalCount} results
        </div>
        <div className="flex space-x-2">
          <button
            onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
            disabled={currentPage === 1}
            className="px-3 py-1 text-sm border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
          >
            {t("back")}
          </button>
          {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
            const page = i + 1;
            return (
              <button
                key={page}
                onClick={() => setCurrentPage(page)}
                className={`px-3 py-1 text-sm border rounded-md ${
                  currentPage === page
                    ? "bg-blue-500 text-white border-blue-500"
                    : "border-gray-300 hover:bg-gray-50"
                }`}
              >
                {page}
              </button>
            );
          })}
          <button
            onClick={() =>
              setCurrentPage((prev) => Math.min(prev + 1, totalPages))
            }
            disabled={currentPage === totalPages}
            className="px-3 py-1 text-sm border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
          >
            {t("next")}
          </button>
        </div>
      </div>
    );
  };

  if (loading) {
    return <LoadingData />;
  }

  return (
    <div className="max-w-7xl mx-auto p-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">
          Visitor Requests Management
        </h1>
        <p className="text-gray-600">Review and manage all visitor requests</p>
      </div>

      {/* Tabs */}
      <div className="flex space-x-1 mb-6">
        <button
          onClick={() => {
            setActiveTab("visitor");
            setCurrentPage(1);
          }}
          className={`px-6 py-2 rounded-full text-sm font-medium transition-colors ${
            activeTab === "visitor"
              ? "bg-blue-500 text-white"
              : "bg-gray-100 text-gray-600 hover:bg-gray-200"
          }`}
        >
          Visitor
        </button>
        <button
          onClick={() => {
            setActiveTab("vehicle");
            setCurrentPage(1);
          }}
          className={`px-6 py-2 rounded-full text-sm font-medium transition-colors ${
            activeTab === "vehicle"
              ? "bg-blue-500 text-white"
              : "bg-gray-100 text-gray-600 hover:bg-gray-200"
          }`}
        >
          Vehicle
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          {/* Search */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Search
            </label>
            <input
              type="text"
              placeholder="Search by visitor name"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* Site Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select a Site
            </label>
            <Select
              options={sitesOptions}
              value={siteId}
              onChange={(e: { label: string; value: number } | null) => {
                setSiteId(e);
                setCurrentPage(1);
              }}
              className="w-full"
              placeholder="All Sites"
              isClearable
            />
          </div>

          {/* Start Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Start Date
            </label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => {
                setStartDate(e.target.value);
                setCurrentPage(1);
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* End Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              End Date
            </label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => {
                setEndDate(e.target.value);
                setCurrentPage(1);
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* Approval Status */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Status
            </label>
            <select
              value={approvalStatus}
              onChange={(e) => {
                setApprovalStatus(e.target.value);
                setCurrentPage(1);
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">All Status</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>
        </div>
      </div>

      {/* Results */}
      {visitorRequests?.length === 0 ? (
        <div className="text-center py-12">
          <div className="text-gray-400 text-lg mb-2">
            No visitor requests found
          </div>
          <p className="text-gray-500">Try adjusting your search criteria</p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {visitorRequests?.map((request) => (
              <VisitorRequestCard key={request.id} request={request} />
            ))}
          </div>
          <Pagination />
        </>
      )}

      {/* Status Update Modal */}
      {showStatusModal && selectedRequest && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold text-gray-900">
                Update Request Status
              </h2>
              <button
                onClick={() => setShowStatusModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <FiX className="w-6 h-6" />
              </button>
            </div>

            <div className="mb-6">
              <div className="bg-gray-50 p-3 rounded-md mb-4">
                <p className="text-sm text-gray-700">
                  <strong>Visitor:</strong> {selectedRequest.name}
                </p>
                <p className="text-sm text-gray-700">
                  <strong>Reason:</strong> {selectedRequest.reason}
                </p>
                <p className="text-sm text-gray-700">
                  <strong>Date:</strong>{" "}
                  {format(new Date(selectedRequest.startDate), "MMM dd, yyyy")}{" "}
                  - {format(new Date(selectedRequest.endDate), "MMM dd, yyyy")}
                </p>
                <p className="text-sm text-gray-700">
                  <strong>Current Status:</strong>{" "}
                  <StatusBadge status={selectedRequest.current_status} />
                </p>
              </div>

              {/* New Status Selection */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  New Status *
                </label>
                <select
                  value={newStatus}
                  onChange={(e) =>
                    setNewStatus(
                      e.target.value as "pending" | "approved" | "rejected"
                    )
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">Select a Status</option>
                  <option value="approved">Approved</option>
                  <option value="rejected">Rejected</option>
                </select>
              </div>

              {/* Status Reason */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Reason {newStatus === "rejected" && "*"}
                </label>
                <textarea
                  value={statusReason}
                  onChange={(e) => setStatusReason(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  rows={3}
                  placeholder={
                    newStatus === "rejected"
                      ? "Please provide a reason for rejection"
                      : "Optional reason for status change"
                  }
                  required={newStatus === "rejected"}
                />
              </div>
            </div>

            <div className="flex space-x-3">
              <button
                type="button"
                onClick={() => setShowStatusModal(false)}
                className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={updateRequestStatus}
                disabled={
                  statusLoading ||
                  (newStatus === "rejected" && !statusReason.trim())
                }
                className="flex-1 px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {statusLoading ? "Updating..." : "Update Status"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
